import { MessageType as ChatMessageType } from './message';

/**
 * WebSocket消息类型枚举
 * 专门用于WebSocket消息传输，与SSE消息类型分离
 */
export enum WSMessageType {
  UNKNOWN = 0,
  CHAT = 1,
  GROUP_MESSAGE = 2,
  TYPING = 3,
  HEARTBEAT = 4,
  MESSAGE_ACK = 5,
  CONNECT = 6,
  NOTICE = 7,
  UNREAD_COUNT_UPDATE = 8,
  FRIEND_REQUEST = 9,
  FRIEND_STATUS_CHANGE = 10,
  GROUP_JOIN_REQUEST = 11,
  GROUP_JOIN_REQUEST_RESPONSE = 12,
  ANALYSIS_UPDATE = 13,
  GET_CHAT_HISTORY = 22,
  CHAT_HISTORY = 23,
  MARK_AS_READ = 24,
  CREATE_PRIVATE_CONVERSATION = 30,
  CREATE_GROUP_CONVERSATION = 31,
  CONVERSATION_CREATED = 32,
  GET_UNREAD_COUNTS = 33,
  UNREAD_COUNTS = 34,
  GET_PRIVATE_CONVERSATIONS = 35,
  PRIVATE_CONVERSATIONS_LIST = 36,
  GET_GROUP_CONVERSATIONS = 37,
  GROUP_CONVERSATIONS_LIST = 38
}

/**
 * WebSocket消息接口
 */
export interface WSMessage {
  messageType: WSMessageType;
  messageContent: any;
  timestamp?: number;
  messageId?: string;
}

/**
 * 创建WebSocket消息
 */
export function createWSMessage(
  messageType: WSMessageType,
  messageContent: any,
  messageId?: string
): WSMessage {
  return {
    messageType,
    messageContent,
    timestamp: Date.now(),
    messageId: messageId || generateMessageId()
  };
}

/**
 * 生成消息ID
 */
function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 解析WebSocket消息
 */
export function parseWSMessage(data: string): WSMessage | null {
  try {
    const message = JSON.parse(data) as WSMessage;
    
    const validTypes = [
      WSMessageType.UNKNOWN,
      WSMessageType.CHAT,
      WSMessageType.GROUP_MESSAGE,
      WSMessageType.TYPING,
      WSMessageType.HEARTBEAT,
      WSMessageType.MESSAGE_ACK,
      WSMessageType.CONNECT,
      WSMessageType.NOTICE,
      WSMessageType.UNREAD_COUNT_UPDATE,
      WSMessageType.FRIEND_REQUEST,
      WSMessageType.FRIEND_STATUS_CHANGE,
      WSMessageType.GROUP_JOIN_REQUEST,
      WSMessageType.GROUP_JOIN_REQUEST_RESPONSE,
      WSMessageType.ANALYSIS_UPDATE,
      WSMessageType.GET_CHAT_HISTORY,
      WSMessageType.CHAT_HISTORY,
      WSMessageType.MARK_AS_READ,
      WSMessageType.CREATE_PRIVATE_CONVERSATION,
      WSMessageType.CREATE_GROUP_CONVERSATION,
      WSMessageType.CONVERSATION_CREATED,
      WSMessageType.GET_UNREAD_COUNTS,
      WSMessageType.UNREAD_COUNTS,
      WSMessageType.GET_PRIVATE_CONVERSATIONS,
      WSMessageType.PRIVATE_CONVERSATIONS_LIST,
      WSMessageType.GET_GROUP_CONVERSATIONS,
      WSMessageType.GROUP_CONVERSATIONS_LIST
    ];
    
    if (!validTypes.includes(message.messageType)) {
      console.warn('未知WebSocket消息类型:', message.messageType);
      return null;
    }
    
    return message;
  } catch (error) {
    console.error('解析WebSocket消息失败:', error);
    return null;
  }
}

/**
 * 判断是否为心跳消息
 */
export function isHeartbeatWSMessage(message: WSMessage): boolean {
  return message.messageType === WSMessageType.HEARTBEAT;
}

/**
 * 判断是否为聊天消息
 */
export function isChatWSMessage(message: WSMessage): message is WSMessage & {
  messageContent: ChatMessage;
} {
  return message.messageType === WSMessageType.CHAT;
}

/**
 * 判断是否为群组消息
 */
export function isGroupMessageWSMessage(message: WSMessage): message is WSMessage & {
  messageContent: ChatMessage;
} {
  return message.messageType === WSMessageType.GROUP_MESSAGE;
}

/**
 * 判断是否为输入指示消息
 */
export function isTypingWSMessage(message: WSMessage): message is WSMessage & {
  messageContent: TypingIndicator;
} {
  return message.messageType === WSMessageType.TYPING;
}

/**
 * 聊天消息接口
 */
export interface ChatMessage {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  messageType: ChatMessageType;
  conversationId: string;
  timestamp: number;
  status: string;
  receiverNickName?: string;
  // Image message fields
  imageUrl?: string;
  thumbnailUrl?: string;
  thumbnailBase64?: string;
  imageWidth?: number;
  imageHeight?: number;
  width?: number;
  height?: number;
  // File message fields
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  mimeType?: string;
}

/**
 * 输入指示消息接口
 */
export interface TypingIndicator {
  userId: number;
  isTyping: boolean;
}

/**
 * 未读计数更新消息接口（单个会话）
 */
export interface UnreadCountUpdate {
  conversationId: string;
  unreadCount: number;
  totalCount: number;
  timestamp: number;
}

/**
 * 未读计数更新消息接口（全部会话）
 */
export interface UnreadCountUpdateAll {
  unreadCounts: Record<string, number>;
  totalCount: number;
  timestamp: number;
}

export interface SocialEvent {
  eventId?: string;
  eventType?: number;
  targetUserId?: number;
  actorUserId?: number;
  relatedId?: string;
  remark?: string;
  status?: number;
  timestamp?: number;
  groupId?: number;
}
